// Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "./Navbar.css";
import logo from "../../assets/logo.png";

/*
  Navbar Component

  Purpose:
  Provides primary site navigation for both desktop and mobile layouts.
  Handles active link indicator positioning, mobile menu toggling,
  and responsive CTA presentation.

  Notes:
  - Styling and layout behavior are defined in Navbar.css
  - JavaScript is responsible for:
      - Tracking active route changes
      - Positioning the active navigation indicator
      - Managing mobile menu open and close state
      - Locking page scroll while the mobile drawer is open
*/

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/pricelist", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/permanent-jewelry", label: "Permanent Jewelry" },
  { to: "/policy", label: "Policy" },
  { to: "/contact-me", label: "Contact" }
];

function Navbar() {
  const location = useLocation();

  /*
    Desktop nav reference for the active link indicator.
    We keep this scoped to the desktop nav container so the mobile drawer
    does not interfere with indicator measurements.
  */
  const desktopNavRef = useRef(null);

  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  /*
    Page scroll lock when the mobile drawer is open.
    This prevents background scroll and eliminates the weird scroll behavior.
  */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  /*
    Active Link Indicator Effect (desktop only)

    Calculates the position and width of the currently active
    desktop navigation link and updates the indicator style.

    Runs:
    - On initial mount
    - Whenever the route changes
    - On window resize
  */
  useEffect(() => {
    const updateIndicator = () => {
      const activeLink = desktopNavRef.current?.querySelector(".nav-center a.active");

      if (activeLink) {
        const { offsetLeft, offsetWidth } = activeLink;

        setIndicatorStyle({
          left: `${offsetLeft}px`,
          width: `${offsetWidth}px`,
          opacity: 1
        });
      } else {
        setIndicatorStyle({
          width: "0px",
          opacity: 0
        });
      }
    };

    updateIndicator();
    window.addEventListener("resize", updateIndicator);

    return () => {
      window.removeEventListener("resize", updateIndicator);
    };
  }, [location]);

  /*
    Close the drawer on route change as a safety net.
    This covers cases like programmatic navigation or back button behavior.
  */
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => {
    setMobileOpen(prev => !prev);
  };

  const closeMenu = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <nav className="navbar default-nav">
        <div className="nav-spacer"></div>

        <div className="navbar-inner">
          <div className="nav-left">
            <img src={logo} alt="Site logo" className="logo overlapping-logo" />
          </div>

          {/* Desktop center navigation */}
          <div className="nav-center-wrapper" ref={desktopNavRef}>
            <ul className="nav-center">
              {NAV_LINKS.map(link => (
                <NavLink key={link.to} to={link.to} end={link.end}>
                  {link.label}
                </NavLink>
              ))}
            </ul>

            <span className="nav-indicator" style={indicatorStyle}></span>
          </div>

          {/* Desktop right side actions */}
          <div className="nav-right desktop-only">
            <NavLink to="/giftcards" className="cta-button giftcards">
              Gift Cards
            </NavLink>
            <NavLink to="/book" className="cta-button book-now">
              Book Now
            </NavLink>
          </div>

          {/* Mobile persistent Book Now button */}
          <div className="mobile-actions-anchor">
            <NavLink
              to="/book"
              className="cta-button book-now mobile-book-fixed"
              onClick={closeMenu}
            >
              Book Now
            </NavLink>

            <button
              className={`hamburger ${mobileOpen ? "open" : ""}`}
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer lives outside the navbar so it can sit under the navbar background */}
      <div
        id="mobile-drawer"
        className={`mobile-drawer ${mobileOpen ? "open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-drawer-inner">
          <ul className="mobile-nav" onClick={closeMenu}>
            {NAV_LINKS.map(link => (
              <NavLink key={link.to} to={link.to} end={link.end}>
                {link.label}
              </NavLink>
            ))}
          </ul>

          <div className="mobile-actions">
            <NavLink to="/giftcards" className="mobile-giftcards" onClick={closeMenu}>
              Gift Cards
            </NavLink>

            <NavLink to="/book" className="mobile-book" onClick={closeMenu}>
              Book Now
            </NavLink>
          </div>
        </div>
      </div>
    </>
  );
}

export default Navbar;
